import { CreditCard, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * A compact banner shown to students explaining current borrowing status.
 * Props:
 *  - subscription: from user.subscription (state, days_remaining, price, ...)
 *  - variant: "compact" (default) or "full"
 */
export default function SubscriptionBanner({ subscription, variant = "compact" }) {
    if (!subscription) return null;
    const { state, days_remaining, price } = subscription;

    if (state === "trial") {
        return (
            <div data-testid="sub-banner-trial" className="mb-6 rounded-2xl border border-library/30 bg-library/5 p-4 flex flex-wrap items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-library/10 text-library flex items-center justify-center">
                    <Sparkles className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold text-ink">
                        Free trial active · {days_remaining} day{days_remaining !== 1 && "s"} remaining
                    </div>
                    <div className="text-sm text-ink/60">
                        After the trial, borrowing is ₹{price?.total} / year (₹{price?.base} + GST). You can keep earning coins by posting books.
                    </div>
                </div>
                <Link to="/app/subscription" className="btn-secondary text-sm py-2 px-4" data-testid="sub-banner-manage">
                    View plan
                </Link>
            </div>
        );
    }

    if (state === "subscribed") {
        return (
            <div data-testid="sub-banner-active" className="mb-6 rounded-2xl border border-library/30 bg-library/5 p-4 flex flex-wrap items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-library/10 text-library flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold text-ink">Membership active</div>
                    <div className="text-sm text-ink/60">Renews in {days_remaining} day{days_remaining !== 1 && "s"}. Borrow freely.</div>
                </div>
                <Link to="/app/subscription" className="btn-ghost text-sm" data-testid="sub-banner-manage">Manage</Link>
            </div>
        );
    }

    return (
        <div data-testid="sub-banner-expired" className="mb-6 rounded-2xl border border-brick/30 bg-brick/5 p-4 flex flex-wrap items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-brick/10 text-brick flex items-center justify-center">
                <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-[220px]">
                <div className="font-semibold text-ink">Your free trial has ended.</div>
                <div className="text-sm text-ink/60">
                    Pay ₹{price?.total} / year (₹{price?.base} + GST) to keep borrowing.
                    You can still post books and earn coins, and return anything you already borrowed.
                </div>
            </div>
            <Link to="/app/subscription" className="btn-primary text-sm py-2 px-4" data-testid="sub-banner-pay">
                <CreditCard className="h-4 w-4" /> Pay ₹{price?.total}
            </Link>
        </div>
    );
}
